/**
 * Hero visual, before/after variant.
 *
 * The "before" sheet is a deliberately plain Word-style CV built in HTML: it
 * stands for the visitor's current document, so it must not look like any
 * ClarCV template. The "after" sheet is a real template screenshot from
 * `generate_template_thumbnails`.
 */

import React from 'react';
import PropTypes from 'prop-types';
import './LandingHeroBeforeAfter.css';

const AFTER = { slug: 'silicon_valley_startup', name: 'Silicon Valley' };

const COPY = {
  es: {
    before: 'Tu CV ahora',
    after: 'Con ClarCV',
    alt: 'CV de ejemplo generado con ClarCV',
    doc: {
      title: 'CURRICULUM VITAE',
      personal: 'DATOS PERSONALES',
      fields: [
        'Nombre y apellidos: Marina López García',
        'DNI: 47.XXX.XXX-K',
        'Fecha de nacimiento: 12/03/1996',
        'Estado civil: Soltera',
        'Dirección: C/ Ejemplo 24, 3º B — Barcelona',
        'Teléfono: 6XX XX XX XX',
        'Email: marinalopez_96@hotmail.com',
        'Carnet de conducir B y vehículo propio',
      ],
      goalTitle: 'OBJETIVO PROFESIONAL',
      goal:
        'Busco un puesto de trabajo en una empresa dinámica donde poder desarrollarme profesionalmente y aportar todos mis conocimientos, con posibilidades de crecimiento dentro de la misma.',
      expTitle: 'EXPERIENCIA LABORAL',
      exp: [
        '2021 - Actualidad. Nimbus. Atención al cliente. Mis funciones eran atender a los clientes, resolver las incidencias que llegaban, hacer seguimiento de las cuentas y pasar los informes.',
        '2019 - 2021. Orbit. Departamento de soporte. Encargada de responder los tickets y de elaborar el reporte semanal.',
        '2018. Retail Norte. Prácticas de empresa. Atención en tienda y control de pedidos.',
      ],
      eduTitle: 'FORMACIÓN ACADÉMICA',
      edu: [
        '2015 - 2019. Administración y Dirección de Empresas. Universitat Pompeu Fabra.',
        '2013 - 2015. Bachillerato de Ciencias Sociales. IES Ejemplo.',
      ],
      otherTitle: 'OTROS DATOS DE INTERÉS',
      other: [
        'Informática: nivel usuario. Paquete Office.',
        'Idiomas: inglés nivel medio.',
        'Persona responsable, trabajadora y con don de gentes.',
      ],
    },
  },
  en: {
    before: 'Your CV today',
    after: 'With ClarCV',
    alt: 'Sample CV generated with ClarCV',
    doc: {
      title: 'CURRICULUM VITAE',
      personal: 'PERSONAL DETAILS',
      fields: [
        'Full name: Marina López García',
        'ID number: 47.XXX.XXX-K',
        'Date of birth: 12/03/1996',
        'Marital status: Single',
        'Address: 24 Example St, Flat 3B — Barcelona',
        'Phone: 6XX XX XX XX',
        'Email: marinalopez_96@hotmail.com',
        'Driving licence and own car',
      ],
      goalTitle: 'CAREER OBJECTIVE',
      goal:
        'I am looking for a position in a dynamic company where I can develop professionally and contribute all of my knowledge, with the possibility of growing within it.',
      expTitle: 'WORK EXPERIENCE',
      exp: [
        '2021 - Present. Nimbus. Customer service. My duties were to attend to customers, resolve the incidents that came in, follow up on accounts and pass on the reports.',
        '2019 - 2021. Orbit. Support department. In charge of answering tickets and putting together the weekly report.',
        '2018. Retail Norte. Work placement. Shop floor assistance and order control.',
      ],
      eduTitle: 'EDUCATION',
      edu: [
        '2015 - 2019. Business Administration. Pompeu Fabra University.',
        '2013 - 2015. Social Sciences Baccalaureate. IES Ejemplo.',
      ],
      otherTitle: 'OTHER INFORMATION',
      other: [
        'Computing: user level. Office suite.',
        'Languages: intermediate English.',
        'Responsible, hard-working and a real people person.',
      ],
    },
  },
};

const LandingHeroBeforeAfter = ({ lang }) => {
  const copy = COPY[lang === 'en' ? 'en' : 'es'];
  const doc = copy.doc;

  return (
    <div className="hero-ba">
      <figure className="hero-ba__item hero-ba__item--before">
        <div className="hero-ba__frame">
          <div className="hero-ba__paper">
            <p className="hero-ba__doc-title">{doc.title}</p>
            <div className="hero-ba__doc-photo" />
            <p className="hero-ba__doc-head">{doc.personal}</p>
            {doc.fields.map((field) => (
              <p key={field} className="hero-ba__doc-line">
                {field}
              </p>
            ))}
            <p className="hero-ba__doc-head">{doc.goalTitle}</p>
            <p className="hero-ba__doc-para">{doc.goal}</p>
            <p className="hero-ba__doc-head">{doc.expTitle}</p>
            {doc.exp.map((item) => (
              <p key={item} className="hero-ba__doc-para">
                {item}
              </p>
            ))}
            <p className="hero-ba__doc-head">{doc.eduTitle}</p>
            {doc.edu.map((item) => (
              <p key={item} className="hero-ba__doc-line">
                {item}
              </p>
            ))}
            <p className="hero-ba__doc-head">{doc.otherTitle}</p>
            {doc.other.map((item) => (
              <p key={item} className="hero-ba__doc-line">
                {item}
              </p>
            ))}
          </div>
        </div>
        <figcaption className="hero-ba__label hero-ba__label--before">{copy.before}</figcaption>
      </figure>

      <figure className="hero-ba__item hero-ba__item--after">
        <img
          className="hero-ba__shot"
          src={`/templates/${AFTER.slug}.webp`}
          alt={copy.alt}
          width="620"
          height="877"
          fetchPriority="high"
          decoding="async"
        />
        <figcaption className="hero-ba__label hero-ba__label--after">{copy.after}</figcaption>
      </figure>
    </div>
  );
};

LandingHeroBeforeAfter.propTypes = {
  lang: PropTypes.string,
};

LandingHeroBeforeAfter.defaultProps = {
  lang: 'es',
};

export default LandingHeroBeforeAfter;
